<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import FileInput from '@uiKit/elements/FileInput.vue'
import { UploadIcon } from '@uiKit/icons'
import {
  SUPPORT_TICKET_ATTACHMENT_ACCEPT,
  SUPPORT_TICKET_ATTACHMENT_EXTENSIONS,
  SUPPORT_TICKET_ATTACHMENT_MAX_MB,
} from '../constants/supportTickets'

// The ticket form's optional attachment (Figma 36076:127996): a dashed lavender
// drag-or-click panel that swaps for a file row once something is picked.
//
// Drop and pick both land on the one v-model, and both are checked against the
// accepted types and the size limit here, so the seller learns which file was
// wrong before anything is sent.
defineProps<{ disabled?: boolean }>()

const file = defineModel<File | null>({ required: true })

const BYTES_PER_MB = 1024 * 1024

const isDragging = ref(false)
const rejection = ref('')

function rejectionFor(candidate: File): string {
  const name = candidate.name.toLowerCase()
  if (!SUPPORT_TICKET_ATTACHMENT_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return 'That file type can’t be attached. Use a PDF, JPEG or PNG.'
  }
  if (candidate.size > SUPPORT_TICKET_ATTACHMENT_MAX_MB * BYTES_PER_MB) {
    return `That file is over ${SUPPORT_TICKET_ATTACHMENT_MAX_MB} MB. Attach a smaller one.`
  }
  return ''
}

function accept(candidate: File | null) {
  if (!candidate) {
    file.value = null
    return
  }
  rejection.value = rejectionFor(candidate)
  file.value = rejection.value ? null : candidate
}

function onDrop(event: DragEvent) {
  isDragging.value = false
  accept(event.dataTransfer?.files[0] ?? null)
}

function sizeLabel(bytes: number): string {
  return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`
}
</script>

<template>
  <FileInput
    :model-value="file"
    :accept="SUPPORT_TICKET_ATTACHMENT_ACCEPT"
    @update:model-value="accept"
  >
    <template #default="{ pick, clear, file: picked }">
      <Box v-if="picked" flex align="center" gap="3" class="attachment__row">
        <Box flex align="center" justify="center" class="attachment__thumb">
          <PiIcon name="file" class="attachment__thumb-icon" />
        </Box>
        <Box direction="col" gap="1" grow class="attachment__info">
          <Text is="span" type="body" weight="semibold" truncate>{{ picked.name }}</Text>
          <Text is="span" type="caption" color="muted">{{ sizeLabel(picked.size) }}</Text>
        </Box>
        <Button
          label="Remove"
          variant="outline"
          color="secondary"
          size="small"
          :disabled="disabled"
          @click="clear"
        />
      </Box>

      <Box v-else direction="col" gap="2" class="attachment__stack">
        <Box
          is="button"
          type="button"
          flex
          direction="col"
          align="center"
          justify="center"
          gap="2"
          class="attachment__dropzone"
          :class="{ 'attachment__dropzone--dragging': isDragging }"
          :disabled="disabled"
          @click="pick"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
        >
          <UploadIcon class="attachment__icon" aria-hidden="true" />
          <Text is="span" type="heading" weight="semibold">
            Drag and drop or click to upload a file
          </Text>
          <Text is="span" type="body" color="var(--p-sliceal-text-subtle)">
            PDF, JPEG, or PNG · Max {{ SUPPORT_TICKET_ATTACHMENT_MAX_MB }} MB
          </Text>
        </Box>
        <Text v-if="rejection" type="caption" color="danger" role="alert">{{ rejection }}</Text>
      </Box>
    </template>
  </FileInput>
</template>

<style scoped>
.attachment__stack {
  width: 100%;
}

.attachment__dropzone {
  width: 100%;
  padding: 1.75rem 1.25rem;
  border: 1.5px dashed var(--p-primary-300);
  border-radius: 12px;
  background: var(--p-primary-50);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.attachment__dropzone:hover:not(:disabled) {
  border-color: var(--p-primary-500);
}

.attachment__dropzone:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.attachment__dropzone:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* A file is over the panel: the wash deepens so the drop lands where it looks. */
.attachment__dropzone--dragging {
  border-color: var(--p-primary-600);
  background: var(--p-primary-100);
}

.attachment__icon {
  width: 22px;
  height: 19px;
  color: var(--p-primary-600);
}

.attachment__row {
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  background: var(--p-surface-0);
}

.attachment__thumb {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: var(--p-border-radius-md);
  background: var(--p-primary-50);
}

.attachment__thumb-icon {
  color: var(--p-primary-600);
}

.attachment__info {
  min-width: 0;
}
</style>
